import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Platform, PlatformGapReport } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, attachmentUrl } from "../services/expensesService";
import { deletePlatformInvoice, fetchPlatformInvoices, type PlatformInvoiceRow } from "../services/platformBillingService";
import { Chip, Dialog, ErrorBox } from "./ui";
import { money, monthLabel } from "../lib/format";
import { GAP_TONES, KIND_TONES } from "../lib/platformBilling";
import { ui } from "../theme";
import { PLATFORMS_NS } from "../locales/platformsI18n";

// Historial de facturas de una plataforma y sus faltantes por mes (#957).

export default function PlatformInvoiceHistory({
  platform,
  gaps,
  onClose,
  onChanged,
}: {
  platform: Platform | null;
  gaps?: PlatformGapReport;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t: tp, i18n } = useTranslation(PLATFORMS_NS);
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const [rows, setRows] = useState<PlatformInvoiceRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!platform) return;
    try {
      setRows(await fetchPlatformInvoices({ platformId: platform.id }));
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    }
  }, [platform, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (r: PlatformInvoiceRow) => {
    if (!window.confirm(tp("invoices.confirmRemove"))) return;
    try {
      await deletePlatformInvoice(r.id);
      await load();
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.save"));
    }
  };

  const openFile = async (path: string) => {
    try {
      const { url } = await attachmentUrl(path);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setError(apiErrorMessage(err) || t("expenses.errors.load"));
    }
  };

  return (
    <Dialog open={Boolean(platform)} title={`${tp("invoices.history")} · ${platform?.name || ""}`} onClose={onClose} wide>
      <div className="space-y-4">
        <ErrorBox message={error} />
        {gaps && (
          <div>
            <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{tp("invoices.gapsTitle")}</h3>
            <div className="flex flex-wrap gap-2">
              {gaps.months.map((m) => (
                <div key={m.period} className={`rounded-lg border px-2 py-1 text-xs ${c.border}`}>
                  <p className={c.muted}>{monthLabel(m.period, i18n.language)}</p>
                  <Chip className={GAP_TONES[m.status]}>{tp(`gap.${m.status}`)}</Chip>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className={`text-left text-xs ${c.muted}`}>
                <th className="px-2 py-2">{tp("invoices.issueDate")}</th>
                <th className="px-2 py-2">{tp("invoices.number")}</th>
                <th className="px-2 py-2">{tp("invoices.period")}</th>
                <th className="px-2 py-2 text-right">{tp("invoices.total")}</th>
                <th className="px-2 py-2">{tp("invoices.linkedTo")}</th>
                <th className="px-2 py-2">{tp("invoices.files")}</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-t ${c.border} ${c.text}`}>
                  <td className="whitespace-nowrap px-2 py-2">{r.data?.issueDate || "—"}</td>
                  <td className="px-2 py-2">
                    <p>{r.data?.invoiceNumber || r.data?.uuid || "—"}</p>
                    <Chip className={KIND_TONES[r.data?.kind || "factura_extranjera"]}>
                      {tp(`invoices.kind_${r.data?.kind || "factura_extranjera"}`)}
                    </Chip>{" "}
                    <Chip className={r.status === "confirmada" ? "bg-gray-400/15 text-gray-500" : "bg-amber-500/15 text-amber-600"}>{tp(`invoices.status_${r.status}`)}</Chip>
                  </td>
                  <td className={`whitespace-nowrap px-2 py-2 text-xs ${c.muted}`}>
                    {r.data?.periodFrom ? `${r.data.periodFrom} → ${r.data.periodTo || "…"}` : "—"}
                    {r.data?.seats != null && <p>{`${tp("invoices.seats")}: ${r.data.seats}`}</p>}
                    {r.data?.usageQuantity != null && <p>{`${tp("invoices.usage")}: ${r.data.usageQuantity} ${r.data.usageUnit || ""}`}</p>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{money(r.data?.total ?? null, r.data?.currency || "MXN", i18n.language)}</td>
                  <td className="whitespace-nowrap px-2 py-2 text-xs">
                    {r.expense ? `${r.expense.date} · ${money(r.expense.amount, r.expense.currency, i18n.language)}` : <span className="text-sky-600">{tp("gap.falta_cargo")}</span>}
                  </td>
                  <td className="px-2 py-2">
                    {(r.files || []).map((f) => (
                      <button key={f.path} type="button" className="mr-2 text-xs text-teal-600 hover:underline" onClick={() => openFile(f.path)}>
                        {f.name}
                      </button>
                    ))}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <button type="button" aria-label={tp("invoices.remove")} title={tp("invoices.remove")} className="text-red-500" onClick={() => remove(r)}>
                      <i className="ri-delete-bin-line" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className={`px-2 py-6 text-center ${c.muted}`}>
                    {tp("invoices.historyEmpty")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Dialog>
  );
}
